import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, FlaskConical, ArrowLeft, Database, ListRestart, Crosshair, Radar, BarChart2, Flame, Waves } from 'lucide-react';

const Container = styled.div`
  min-height: 100vh;
  background: #0f0f0f;
  color: #e5e5e5;
  padding: 20px;
  font-family: 'Inter', sans-serif;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #333;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 800;
  background: linear-gradient(135deg, #00c6ff, #0072ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 16px;
`;

const LabCard = styled.div`
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  items-align: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  height: 160px;
  text-align: center;

  &:hover {
    transform: translateY(-4px);
    border-color: #0072ff;
    box-shadow: 0 10px 20px rgba(0, 114, 255, 0.2);
  }
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  background: rgba(0, 114, 255, 0.1);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: #0072ff;
`;

const CardTitle = styled.h3`
  font-size: 16px;
  margin: 0 0 8px;
  color: #fff;
`;

const CardDesc = styled.p`
  font-size: 13px;
  color: #888;
  margin: 0;
  line-height: 1.4;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #888;
  cursor: pointer;
  padding: 8px;
  &:hover { color: #fff; }
`;

function LabsPage() {
  const navigate = useNavigate();

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/')}><ArrowLeft /></BackButton>
        <FlaskConical size={24} color="#0072ff" />
        <Title>Nureum Labs</Title>
      </Header>

      <Grid>
        <LabCard onClick={() => navigate('/labs/stock')}>
          <IconWrapper>
            <TrendingUp size={24} />
          </IconWrapper>
          <CardTitle>Stock Brief</CardTitle>
          <CardDesc>매일 아침,<br />장전 주식 분석</CardDesc>
        </LabCard>

        <LabCard onClick={() => navigate('/labs/stock/technical')}>
          <IconWrapper>
            <TrendingUp size={24} />
          </IconWrapper>
          <CardTitle>Technical Chart</CardTitle>
          <CardDesc>전문가용 차트 &<br />지표 분석</CardDesc>
        </LabCard>

        <LabCard onClick={() => navigate('/data-generator')}>
          <IconWrapper>
            <Database size={24} />
          </IconWrapper>
          <CardTitle>Data Generator</CardTitle>
          <CardDesc>가격 데이터 생성 및<br />스케쥴링 관리</CardDesc>
        </LabCard>

        <LabCard onClick={() => navigate('/labs/backtest')}>
          <IconWrapper>
            <ListRestart size={24} />
          </IconWrapper>
          <CardTitle>Backtest Optimizer</CardTitle>
          <CardDesc>가장 수익률이 높은<br />최적 지표 찾기</CardDesc>
        </LabCard>

        <LabCard onClick={() => navigate('/labs/screener')}>
          <IconWrapper style={{ background: 'rgba(34, 197, 94, 0.1)', color: '#22c55e' }}>
            <Crosshair size={24} />
          </IconWrapper>
          <CardDesc>전략 적용 후<br />매수 종목 탐색</CardDesc>
        </LabCard>

        <LabCard onClick={() => navigate('/labs/moneyflow')}>
          <IconWrapper style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}>
            <TrendingUp size={24} />
          </IconWrapper>
          <CardTitle>Money Flow</CardTitle>
          <CardDesc>실시간 수급 및<br />자금 이동 대시보드</CardDesc>
        </LabCard>

        <LabCard onClick={() => navigate('/labs/livermore')}>
          <IconWrapper style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}>
            <Crosshair size={24} />
          </IconWrapper>
          <CardTitle>Livermore Finder</CardTitle>
          <CardDesc>제시 리버모어의<br />주도주 및 전환점 탐지</CardDesc>
        </LabCard>


        <LabCard onClick={() => navigate('/labs/livermore-v21')}>
          <IconWrapper style={{ background: 'rgba(6, 182, 212, 0.1)', color: '#06b6d4' }}>
            <TrendingUp size={24} />
          </IconWrapper>
          <CardTitle>Livermore V2.1</CardTitle>
          <CardDesc>V2.0 헛점 보강:<br />52W·뉴스·우선주 필터</CardDesc>
        </LabCard>

        <LabCard onClick={() => navigate('/labs/livermore-v30')}>
          <IconWrapper style={{ background: 'rgba(168, 85, 247, 0.1)', color: '#a855f7' }}>
            <TrendingUp size={24} />
          </IconWrapper>
          <CardTitle>Livermore V3.0</CardTitle>
          <CardDesc>거래량 마름 &<br />이동평균선 필터 강화</CardDesc>
        </LabCard>

        <LabCard onClick={() => navigate('/labs/livermore-v31')}>
          <IconWrapper style={{ background: 'rgba(34, 211, 238, 0.1)', color: '#22d3ee' }}>
            <TrendingUp size={24} />
          </IconWrapper>
          <CardTitle>Livermore V3.1</CardTitle>
          <CardDesc>VCP 필터 적용 +<br />폴백 제거 + 정배열 강화</CardDesc>
        </LabCard>

        <LabCard onClick={() => navigate('/labs/livermore-v40')}>
          <IconWrapper style={{ background: 'rgba(168, 85, 247, 0.1)', color: '#a855f7' }}>
            <TrendingUp size={24} />
          </IconWrapper>
          <CardTitle>Livermore V4.0</CardTitle>
          <CardDesc>점수·랭킹 기반 스캐너<br />탈락 없이 전체 평가</CardDesc>
        </LabCard>

        <LabCard onClick={() => navigate('/labs/wyckoff')}>
          <IconWrapper style={{ background: 'rgba(102, 252, 241, 0.1)', color: '#66fcf1' }}>
            <TrendingUp size={24} />
          </IconWrapper>
          <CardTitle>Wyckoff Spring</CardTitle>
          <CardDesc>와이코프식 속임수<br />이탈(Spring) 탐지</CardDesc>
        </LabCard>

        <LabCard onClick={() => navigate('/labs/turtle')}>
          <IconWrapper style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
            <span style={{ fontSize: '22px' }}>🐢</span>
          </IconWrapper>
          <CardTitle>Turtle Trading</CardTitle>
          <CardDesc>Richard Dennis<br />규칙 기반 가상 자동매매</CardDesc>
        </LabCard>

        <LabCard onClick={() => navigate('/labs/minervini')}>
          <IconWrapper style={{ background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}>
            <span style={{ fontSize: '22px' }}>📐</span>
          </IconWrapper>
          <CardTitle>Minervini VCP</CardTitle>
          <CardDesc>Mark Minervini<br />VCP 패턴 가상 자동매매</CardDesc>
        </LabCard>

        <LabCard onClick={() => navigate('/labs/vwap')}>
          <IconWrapper style={{ background: 'rgba(244, 63, 94, 0.1)', color: '#f43f5e' }}>
            <span style={{ fontSize: '22px' }}>🎯</span>
          </IconWrapper>
          <CardTitle>VWAP Trading</CardTitle>
          <CardDesc>Livermore 주도주 기반<br />VWAP 눌림목 가상매매</CardDesc>
        </LabCard>

        <LabCard onClick={() => navigate('/labs/vwap-screener')}>
          <IconWrapper style={{ background: 'rgba(0, 198, 255, 0.1)', color: '#00c6ff' }}>
            <span style={{ fontSize: '22px' }}>📡</span>
          </IconWrapper>
          <CardTitle>VWAP 스크리너</CardTitle>
          <CardDesc>정배열·기울기·이격도·<br />변동성 수축 급등 탐색</CardDesc>
        </LabCard>

        <LabCard onClick={() => navigate('/labs/dtl-screener')}>
          <IconWrapper style={{ background: 'rgba(251, 191, 36, 0.1)', color: '#fbbf24' }}>
            <Radar size={24} />
          </IconWrapper>
          <CardTitle>DTL-2Stage</CardTitle>
          <CardDesc>EMA·일목·VWAP·CCI<br />주도주 자동 발굴</CardDesc>
        </LabCard>

        <LabCard onClick={() => navigate('/labs/dtl-paper')}>
          <IconWrapper style={{ background: 'rgba(6, 182, 212, 0.1)', color: '#06b6d4' }}>
            <BarChart2 size={24} />
          </IconWrapper>
          <CardTitle>DTL 가상매매</CardTitle>
          <CardDesc>Paper Trading 결과<br />포지션·손익·로그 조회</CardDesc>
        </LabCard>

        {/* 주역×사주 AI */}
        <LabCard onClick={() => navigate('/labs/iching')}>
          <IconWrapper style={{ background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}>
            <span style={{ fontSize: '22px' }}>☯</span>
          </IconWrapper>
          <CardTitle>주역 × 사주 AI</CardTitle>
          <CardDesc>동전점으로 괘를 뽑고<br />AI가 해석합니다</CardDesc>
        </LabCard>

        {/* 상한가 레이더 (limit_up_radar) */}
        <LabCard onClick={() => navigate('/labs/radar')}>
          <IconWrapper style={{ background: 'rgba(249, 115, 22, 0.1)', color: '#f97316' }}>
            <Flame size={24} />
          </IconWrapper>
          <CardTitle>상한가 레이더</CardTitle>
          <CardDesc>상한가 종목 탐지 +<br />상승 사유·지속성 추적</CardDesc>
        </LabCard>

        {/* 기관수급분석 (inst_flow_radar) */}
        <LabCard onClick={() => navigate('/labs/inst-flow')}>
          <IconWrapper style={{ background: 'rgba(6, 182, 212, 0.1)', color: '#06b6d4' }}>
            <Waves size={24} />
          </IconWrapper>
          <CardTitle>기관수급분석</CardTitle>
          <CardDesc>코스피200 선물/옵션<br />기관·외국인 수급 국면 판정</CardDesc>
        </LabCard>

        {/* Future Apps... */}
        <LabCard style={{ opacity: 0.5, cursor: 'not-allowed' }}>
          <IconWrapper style={{ background: '#333', color: '#666' }}>
            <span style={{ fontSize: '20px' }}>?</span>
          </IconWrapper>
          <CardTitle>Coming Soon</CardTitle>
        </LabCard>
      </Grid>
    </Container>
  );
}

export default LabsPage;
